import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Tv, Film, Mic, LogOut, User, ArrowLeft, Users, BookOpen, MessageSquare } from 'lucide-react';
import { Badge } from './ui/badge';
import { MobileNav } from './MobileNav';
import { clearAuth } from '../lib/api';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SortaLogo = ({ size = "default" }) => {
  const sizeClasses = { small: "h-8", default: "h-10", large: "h-14" };
  return (
    <img 
      src="https://customer-assets.emergentagent.com/job_series-ranker-1/artifacts/barwtr5h_image.jpg"
      alt="Sorta Media"
      className={`${sizeClasses[size]} object-contain rounded-lg`}
    />
  );
};

export function AppHeader({ user, showBack = false, backPath = null, backLabel = null }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadMessages, setUnreadMessages] = useState(0);
  
  // Fetch unread message count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (!user) return;
      try {
        const token = localStorage.getItem('auth_token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/messages/unread-count`, { withCredentials: true, headers });
        setUnreadMessages(response.data.unread_count || 0);
      } catch (error) {
        // Silently fail - non-critical
      }
    };
    
    fetchUnreadCount();
    // Poll every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [user]);
  
  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      clearAuth();
      navigate('/');
    } catch (error) {
      clearAuth();
      navigate('/');
    }
  };

  // Determine active page based on current path
  const isActive = (path) => {
    if (path === '/social') return location.pathname === '/social';
    if (path === '/tv-shows') {
      // For watchlists, check if it's a TV list by looking at URL params or state
      if (location.pathname.startsWith('/watchlist/')) {
        // Check for media_category in URL search params
        const searchParams = new URLSearchParams(location.search);
        const mediaCategory = searchParams.get('media_category');
        if (mediaCategory) return mediaCategory === 'tv';
        // If no param, it could be either - don't highlight TV
        return false;
      }
      return location.pathname.startsWith('/tv');
    }
    if (path === '/films') {
      if (location.pathname.startsWith('/watchlist/')) {
        const searchParams = new URLSearchParams(location.search);
        const mediaCategory = searchParams.get('media_category');
        if (mediaCategory) return mediaCategory === 'movie';
        return false;
      }
      return location.pathname.startsWith('/film');
    }
    if (path === '/podcasts') return location.pathname.startsWith('/podcast');
    if (path === '/books') return location.pathname.startsWith('/book');
    return location.pathname === path;
  };

  const handleBack = () => {
    if (backPath) {
      navigate(backPath);
    } else {
      navigate(-1);
    }
  };

  return (
    <header className="bg-[#F87171] sticky top-0 z-50 shadow-lg">
      <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Mobile Menu */}
            <MobileNav user={user} onLogout={handleLogout} />
            
            {/* Back Button (when needed) */}
            {showBack && (
              <Button 
                variant="ghost" 
                onClick={handleBack}
                className="rounded-full text-white hover:bg-white/10 p-2 sm:hidden"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
            )}
            
            {/* Logo */}
            <div 
              className="flex items-center gap-2 cursor-pointer" 
              onClick={() => navigate('/tv-shows')}
            >
              <SortaLogo size={showBack ? "small" : "default"} />
              <span className="hidden sm:block text-white font-outfit font-semibold text-xl">Media</span>
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden sm:flex items-center gap-1 ml-2">
              {/* Social - First in nav */}
              <Button
                variant="ghost"
                onClick={() => navigate('/social')}
                className={`rounded-full ${
                  isActive('/social') 
                    ? 'bg-white/20 text-white hover:bg-white/30' 
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
                data-testid="nav-social"
              >
                <Users className="w-4 h-4 mr-2" />
                Social
              </Button>
              {/* Messages */}
              <Button
                variant="ghost"
                onClick={() => navigate('/messages')}
                className={`rounded-full relative ${
                  location.pathname.startsWith('/messages')
                    ? 'bg-white/20 text-white hover:bg-white/30' 
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
                data-testid="nav-messages"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Messages
                {unreadMessages > 0 && (
                  <Badge className="absolute -top-1 -right-1 bg-yellow-400 text-yellow-900 text-xs px-1.5 min-w-[18px] h-[18px] flex items-center justify-center">
                    {unreadMessages > 9 ? '9+' : unreadMessages}
                  </Badge>
                )}
              </Button>
              {(!user?.interests || user.interests.includes('tv')) && (
                <Button
                  variant="ghost"
                  onClick={() => navigate('/tv-shows')}
                  className={`rounded-full ${
                    isActive('/tv-shows') 
                      ? 'bg-white/20 text-white hover:bg-white/30' 
                      : 'text-white/80 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <Tv className="w-4 h-4 mr-2" />
                  TV Shows
                </Button>
              )}
              {(!user?.interests || user.interests.includes('movies')) && (
                <Button
                  variant="ghost"
                  onClick={() => navigate('/films')}
                  className={`rounded-full ${
                    isActive('/films') 
                      ? 'bg-white/20 text-white hover:bg-white/30' 
                      : 'text-white/80 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <Film className="w-4 h-4 mr-2" />
                  Films
                </Button>
              )}
              {(!user?.interests || user.interests.includes('podcasts')) && (
                <Button
                  variant="ghost"
                  onClick={() => navigate('/podcasts')}
                  className={`rounded-full ${
                    isActive('/podcasts') 
                      ? 'bg-white/20 text-white hover:bg-white/30' 
                      : 'text-white/80 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <Mic className="w-4 h-4 mr-2" />
                  Podcasts
                </Button>
              )}
              {(!user?.interests || user.interests.includes('books')) && (
                <Button
                  variant="ghost"
                  onClick={() => navigate('/books')}
                  className={`rounded-full ${
                    isActive('/books') 
                      ? 'bg-white/20 text-white hover:bg-white/30' 
                      : 'text-white/80 hover:text-white hover:bg-white/10'
                  }`}
                  data-testid="nav-books"
                >
                  <BookOpen className="w-4 h-4 mr-2" />
                  Books
                </Button>
              )}
            </nav>
          </div>
          
          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="rounded-full p-0 w-10 h-10" data-testid="user-menu-btn">
                <Avatar className="border-2 border-white/30">
                  {user?.avatar_url ? (
                    <AvatarImage src={user.avatar_url} alt={user?.display_name || user?.name} />
                  ) : user?.picture ? (
                    <AvatarImage src={user.picture} alt={user?.display_name || user?.name} />
                  ) : (
                    <AvatarFallback className="bg-white text-[#F87171]">
                      {(user?.display_name || user?.name || 'U').charAt(0).toUpperCase()}
                    </AvatarFallback>
                  )}
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-3 py-2 border-b">
                <p className="font-medium text-sm">{user?.display_name || user?.name}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
              <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer">
                <User className="w-4 h-4 mr-2" />
                View Profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer">
                <LogOut className="w-4 h-4 mr-2" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
